"use client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import React, { useCallback, useEffect, useState } from "react";
import { CalendarType, ICalendar, IResource } from "@gettactic/api";
import { Input, InfoTooltip } from "@/ui/index";
import { AlertCircleIcon, CheckCircle2Icon } from "lucide-react";
import { cn } from "@gettactic/helpers/src/css/cn";
import { UnifiedSelect, SelectItem } from "@cmp-src/form/UnifiedSelect";
import { api } from "@/lib/api/api";
import { toast } from "react-hot-toast";
import { useCalendarByResource } from "@gettactic/hooks";
import { useAuthenticated } from "@gettactic/hooks";
import { useAuthorizedIntegrations } from "@/features/reserve/hooks/useCalendar";
import { TextInput } from "@cmp-src/form/TextInput";
import { SETTINGS_ROUTES } from "@gettactic/defines/src/web-routes";
import { buildUrl } from "@gettactic/helpers";
import { getCalendarErrorMessage } from "@/features/reserve/utils/calendar-errors";

export interface CalendarsPanelProps {
  resource: IResource;
  saveRef?: React.RefObject<(() => Promise<void>) | null>;
  onSavingChange?: (isSaving: boolean) => void;
}

export type ConfirmOperationModal = {
  operation: "refresh" | "delete";
};

export const CalendarsPanel = ({
  resource,
  saveRef,
  onSavingChange
}: CalendarsPanelProps) => {
  const {
    userContext: { authenticatedUser, deviceTz }
  } = useAuthenticated();

  const { calendar, isLoading } = useCalendarByResource(resource.id);
  const [integrationIdInputValue, setIntegrationIdInputValue] =
    useState<string>(calendar?.integration_id ? calendar?.integration_id : "");
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [selectedProviderLabel, setSelectedProviderLabel] =
    useState<string>("");

  const handleInputValueChange: React.ChangeEventHandler<HTMLInputElement> =
    useCallback((event) => {
      setIntegrationIdInputValue(event.target.value);
    }, []);

  const providerOptions = [
    { label: "Microsoft Outlook", value: "microsoft" },
    { label: "Google Workspace", value: "google" }
  ];

  const { authorizedIntegrations } = useAuthorizedIntegrations();
  const queryClient = useQueryClient();

  const hasCalendarIntegrations =
    authorizedIntegrations &&
    (authorizedIntegrations?.isMicrosoftAuthorized ||
      authorizedIntegrations?.isGoogleAuthorized);

  useEffect(() => {
    if (calendar?.integration_id) {
      setIntegrationIdInputValue(calendar?.integration_id);
    }
  }, [calendar]);

  useEffect(() => {
    setSelectedProvider(
      authorizedIntegrations?.isMicrosoftAuthorized &&
        authorizedIntegrations?.isGoogleAuthorized
        ? ""
        : !!authorizedIntegrations?.isMicrosoftAuthorized
          ? "microsoft"
          : "google"
    );
    setSelectedProviderLabel(
      authorizedIntegrations?.isMicrosoftAuthorized &&
        authorizedIntegrations?.isGoogleAuthorized
        ? ""
        : !!authorizedIntegrations?.isMicrosoftAuthorized
          ? "Microsoft Outlook"
          : "Google Workspace"
    );
  }, [authorizedIntegrations]);

  const saveCalendar = useMutation({
    mutationFn: async (calendar: ICalendar) => {
      return await api.client.calendar.createOrUpdate(
        calendar.organization_id,
        authenticatedUser.slug || "",
        calendar.source,
        calendar.type,
        calendar.reference_id,
        calendar.integration_id,
        calendar.is_enabled,
        calendar.is_pull_enabled,
        calendar.is_push_enabled,
        calendar.id
      );
    },

    onSuccess: (r) => {
      toast.success("Changes saved");
    },

    onError: async (e) => {
      toast.error(await getCalendarErrorMessage(e));
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["integrations.calendar", resource.id]
      });
    }
  });

  useEffect(() => {
    onSavingChange?.(saveCalendar.isPending);
  }, [saveCalendar.isPending, onSavingChange]);

  useEffect(() => {
    return () => {
      onSavingChange?.(false);
    };
  }, [onSavingChange]);

  const saveCalendarDetails = async () => {
    if (
      integrationIdInputValue === calendar?.integration_id ||
      !integrationIdInputValue
    ) {
      return;
    }

    if (!selectedProvider) {
      toast.error("Please select the Calendar Integration to use");
      return;
    }

    const calendarRequest = {
      organization_id: authenticatedUser.organization?.id,
      source: calendar?.source ?? selectedProvider,
      type: calendar?.type ?? CalendarType.Resource,
      reference_id: resource.id,
      integration_id: integrationIdInputValue,
      is_enabled: calendar?.integration_id ? calendar?.is_enabled : true,
      is_pull_enabled: calendar?.integration_id
        ? calendar?.is_pull_enabled
        : true,
      is_push_enabled: calendar?.integration_id
        ? calendar?.is_push_enabled
        : true,
      id: calendar?.integration_id ? calendar?.id : null
    } as ICalendar;

    await saveCalendar.mutateAsync(calendarRequest).catch(() => {});
  };

  useEffect(() => {
    if (!saveRef) return;
    saveRef.current = saveCalendarDetails;
    return () => {
      saveRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRef, integrationIdInputValue, selectedProvider, calendar]);

  const onSelectedProviderChange = (value: any) => {
    setSelectedProvider(value.value);
    setSelectedProviderLabel(value.label);
  };

  return (
    <div>
      <div className="flex items-center justify-between border-b border-neutral-200 pb-2">
        <h3 className="flex items-center text-2xl font-medium leading-6 text-neutral-900">
          Room Calendars
        </h3>
        <InfoTooltip>
          Adding a room calendar will sync all reservations to and from your
          third party calendar.
        </InfoTooltip>
      </div>
      <div>
        {!hasCalendarIntegrations ? (
          <div className="mt-5 flex flex-col w-full text-center">
            <span>Want to set up Calendar Integrations?</span>
            <br />
            <Link
              href={buildUrl(SETTINGS_ROUTES.integrations, undefined, {
                queryParams: { activeTab: "org-integrations" }
              })}
              legacyBehavior
            >
              <a className="underline"> Visit Integrations page</a>
            </Link>
          </div>
        ) : (
          <div className="flex flex-col w-full text-sm">
            {authorizedIntegrations?.isGoogleAuthorized &&
            authorizedIntegrations.isMicrosoftAuthorized ? (
              <div className="mt-5  w-full justify-between items-center">
                <h3 className="mt-4 text-base font-bold text-neutral-800">
                  Calendar Integration
                </h3>
                <UnifiedSelect
                  items={providerOptions.map((opt) => ({
                    id: opt.value,
                    name: opt.label
                  }))}
                  value={
                    selectedProvider
                      ? { id: selectedProvider, name: selectedProviderLabel }
                      : null
                  }
                  onSelect={(item) =>
                    item &&
                    onSelectedProviderChange({
                      value: item.id,
                      label: item.name
                    })
                  }
                  placeholder="Select a calendar integration..."
                  triggerClassName="w-full rounded-md border border-neutral-300"
                />
              </div>
            ) : null}
            <div className="flex justify-between">
              <h3 className="mt-4 text-base font-bold text-neutral-800">
                {`${selectedProviderLabel}`} Resource Email{" "}
              </h3>
              {calendar?.integration_id && !saveCalendar.isPending ? (
                <div
                  className={cn(
                    calendar?.integration_id
                      ? "text-green-600"
                      : "text-neutral-600",
                    "flex items-center justify-end gap-1 mt-5"
                  )}
                >
                  <span>Connected</span>
                  <CheckCircle2Icon className="h-5 w-5 flex-none" />
                </div>
              ) : calendar?.integration_id && saveCalendar.isPending ? null : (
                <div
                  className={cn(
                    calendar?.integration_id
                      ? "text-green-600"
                      : "text-neutral-600",
                    "flex items-center justify-end gap-1 mt-5"
                  )}
                >
                  <span>Not Connected</span>
                  <AlertCircleIcon className="h-5 w-5 flex-none" />
                </div>
              )}
            </div>
            <TextInput
              className="left-0 mt-4 w-full"
              value={integrationIdInputValue}
              onChange={handleInputValueChange}
              placeholder={`Type or select ${selectedProviderLabel} Resource Email`}
              disabled={saveCalendar.isPending}
            />
          </div>
        )}
      </div>
    </div>
  );
};
